import { useEffect, useState } from "react";

import type { AsyncOption } from "@/components/async-combobox";
import type { RansackFilters } from "@/types";

type SectorFilterKey = "sector_id_eq" | "purchase_request_sector_id_eq";

interface UseSectorFilterOptions {
  filters: RansackFilters;
  handleFilterChange: (newFilters: Partial<RansackFilters>) => void;
  filterKey?: SectorFilterKey;
}

interface UseSectorFilterReturn {
  sectorOption: AsyncOption | null;
  loadSectorOptions: (inputValue: string) => Promise<AsyncOption[]>;
  handleSectorSelect: (option: AsyncOption | null) => void;
  resetSector: () => void;
  hasSectorFilter: boolean;
}

export function useSectorFilter({
  filters,
  handleFilterChange,
  filterKey = "sector_id_eq",
}: UseSectorFilterOptions): UseSectorFilterReturn {
  const [sectorOption, setSectorOption] = useState<AsyncOption | null>(null);

  const currentFilterValue = filters[filterKey] ?? undefined;

  useEffect(() => {
    if (!currentFilterValue) {
      if (sectorOption) {
        setSectorOption(null);
      }
      return;
    }

    if (
      sectorOption &&
      String(sectorOption.value) === String(currentFilterValue)
    ) {
      return;
    }

    let isActive = true;

    const fetchSelectedSector = async () => {
      try {
        const params = new URLSearchParams();
        params.append("ids[]", String(currentFilterValue));
        const response = await fetch(`/sectors/search?${params.toString()}`);
        if (!response.ok) return;
        const data = (await response.json()) as AsyncOption[];
        const match = data.find(
          (option) =>
            String(option.value) === String(currentFilterValue),
        );
        if (isActive) {
          setSectorOption(
            match ? { value: String(match.value), label: match.label } : null,
          );
        }
      } catch {
        if (isActive) {
          setSectorOption(null);
        }
      }
    };

    void fetchSelectedSector();

    return () => {
      isActive = false;
    };
  }, [currentFilterValue]);

  const loadSectorOptions = async (inputValue: string): Promise<AsyncOption[]> => {
    const params = new URLSearchParams();
    if (inputValue) {
      params.set("term", inputValue);
    }
    const currentId =
      currentFilterValue ??
      (sectorOption ? String(sectorOption.value) : undefined);
    if (currentId) {
      params.append("ids[]", String(currentId));
    }

    try {
      const response = await fetch(`/sectors/search?${params.toString()}`, {
        headers: {
          Accept: "application/json",
        },
      });
      if (!response.ok) {
        return [];
      }
      const data = (await response.json()) as AsyncOption[];
      return data.map((option) => ({
        value: String(option.value),
        label: option.label,
      }));
    } catch {
      return [];
    }
  };

  const handleSectorSelect = (option: AsyncOption | null) => {
    setSectorOption(
      option ? { value: String(option.value), label: option.label } : null,
    );
    handleFilterChange({
      [filterKey]: option ? String(option.value) : undefined,
    } as Partial<RansackFilters>);
  };

  const resetSector = () => {
    setSectorOption(null);
  };

  const hasSectorFilter =
    sectorOption !== null || Boolean(currentFilterValue);

  return {
    sectorOption,
    loadSectorOptions,
    handleSectorSelect,
    resetSector,
    hasSectorFilter,
  };
}
